import { useState, useContext, useEffect } from "react";
import { useFormik, Form, FormikProvider } from "formik";
import * as Yup from "yup";
import {
  Grid,
  Button,
  Dialog,
  Select,
  MenuItem,
  TextField,
  Typography,
  FormControl,
  DialogContent,
  DialogActions,
  FormHelperText,
} from "@mui/material";
//Import from project
import "../../Style/dialogstyle.scss";
import moment from "moment";

import { useMutation } from "@apollo/client";
import { AuthContext } from "../../Context/AuthContext";
import { DialogTitleComp } from "../../Utils/Component";
import { SelectDatePicker } from "../Include/SelectDatePicker";
import { CREATE_MEMBER, UPDATE_MEMBER } from "../../Schema/Member";
import { formatPhoneNumber, toLatinUppercase } from "../../Function/DynamicFn";

export default function CreateNewSecretary({
  open,
  onChange = () => {},
  editData,
  setRefetch,
  handleClose,
  dialogTitle,
  hideBackdrop,
}) {
  // Change Language
  const { t, setAlert } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);

  const [createMember] = useMutation(CREATE_MEMBER, {
    onCompleted: ({ createMember }) => {
      setLoading(false);
      if (createMember?.isSuccess === true) {
        if (onChange) {
          onChange({
            id: createMember?.data?._id,
            title:
              createMember?.data?.lastNameKh +
              " " +
              createMember?.data?.firstNameKh,
          });
        }
        setAlert(true, "success", createMember?.message);
        handleClose();
        resetForm();
        setRefetch();
      } else {
        setAlert(true, "error", createMember?.message);
      }
    },
    onError: ({ message }) => {
      setLoading(false);
      setAlert(true, "error", { messageKh: message, messageEn: message });
    },
  });

  const [updateMember] = useMutation(UPDATE_MEMBER, {
    onCompleted: ({ updateMember }) => {
      setLoading(false);
      if (updateMember?.isSuccess === true) {
        setAlert(true, "success", updateMember?.message);
        handleClose();
        resetForm();
        setRefetch();
      } else {
        setAlert(true, "error", updateMember?.message);
      }
    },
    onError: ({ message }) => {
      setLoading(false);
      setAlert(true, "error", { messageKh: message, messageEn: message });
    },
  });

  //fromik
  const CheckValidation = Yup.object().shape({
    role: Yup.string().required(t("required")),
    gender: Yup.string().required(t("required")),
    remark: Yup.string(),
    latinName: Yup.string(),
    lastNameKh: Yup.string().required(t("required")),
    firstNameKh: Yup.string().required(t("required")),
    phoneNumber: Yup.string(),
    dateOfBirth: Yup.string().required(t("required")),
    email: Yup.string()
    
  });

  const formik = useFormik({
    initialValues: {
      role: "Secretary",
      remark: "",
      gender: "Male",
      latinName: "",
      lastNameKh: "",
      dateOfBirth: moment(),
      firstNameKh: "",
      phoneNumber: "0",
      email: "",
    },
    validationSchema: CheckValidation,
    onSubmit: async (values) => {
      setLoading(true);

      const inputData = {
        role: values?.role,
        remark: values?.remark,
        gender: values?.gender,
        latinName: values?.latinName,
        lastNameKh: values?.lastNameKh,
        phoneNumber: values?.phoneNumber,
        firstNameKh: values?.firstNameKh,
        dateOfBirth: moment(values?.dateOfBirth).format("YYYY-MM-DD"),
        email: values?.email,
      };

      if (dialogTitle === "Create") {
        createMember({
          variables: {
            input: {
              ...inputData,
            },
          },
        });
      } else {
        updateMember({
          variables: {
            id: editData?._id,
            input: {
              ...inputData,
            },
          },
        });
      }
    },
  });

  const {
    values,
    errors,
    touched,
    handleSubmit,
    setFieldValue,
    getFieldProps,
    resetForm,
  } = formik;

  useEffect(() => {
    if (editData && dialogTitle === "Update") {
      setFieldValue("remark", editData?.remark || "");
      setFieldValue("gender", editData?.gender || "");
      setFieldValue("latinName", editData?.latinName || "");
      setFieldValue("lastNameKh", editData?.lastNameKh || "");
      setFieldValue("firstNameKh", editData?.firstNameKh || "");
      setFieldValue("dateOfBirth", editData?.dateOfBirth || "");
      setFieldValue("phoneNumber", editData?.phoneNumber || "");
      setFieldValue("email", editData?.email || "");
    }
  }, [open]);

  return (
    <Dialog
      open={open}
      className="dialog-container"
      maxWidth="sm"
      fullWidth
      hideBackdrop={hideBackdrop}
    >
      <DialogTitleComp
        title={
          dialogTitle === "Create"
            ? t("modal_title_create_member")
            : t("modal_title_update_member")
        }
        classTitle={"dialog-title"}
        classIcon={"close-icon"}
        handleClose={handleClose}
      />

      <DialogContent dividers>
        <FormikProvider value={formik}>
          <Form noValidate autoComplete="off" onSubmit={handleSubmit}>
            <Grid container rowSpacing={2} columnSpacing={3}>
              <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                <Typography className={t("class-name-field-title")}>
                  {t("thead_last_name")}
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  className="text-field"
                  placeholder={t("thead_last_name")}
                  {...getFieldProps("lastNameKh")}
                  error={Boolean(touched.lastNameKh && errors.lastNameKh)}
                  helperText={touched.lastNameKh && errors.lastNameKh}
                />
              </Grid>

              <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                <Typography className={t("class-name-field-title")}>
                  {t("thead_first_name")}
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  className="text-field"
                  placeholder={t("thead_first_name")}
                  {...getFieldProps("firstNameKh")}
                  error={Boolean(touched.firstNameKh && errors.firstNameKh)}
                  helperText={touched.firstNameKh && errors.firstNameKh}
                />
              </Grid>

              <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                <Typography className={t("class-name-field-title")}>
                  {t("thead_latin_name")}
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  className="text-field"
                  placeholder={t("thead_latin_name")}
                  value={values.latinName}
                  onChange={(e) => {
                    const input = e.target.value;
                    const uppercased = toLatinUppercase(input);
                    setFieldValue("latinName", uppercased);
                  }}
                  error={Boolean(touched.latinName && errors.latinName)}
                  helperText={touched.latinName && errors.latinName}
                />
              </Grid>

              <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                <Typography className={t("class-name-field-title")}>
                  {t("thead_gender")}
                </Typography>
                <FormControl fullWidth className="search-field" size="small">
                  <Select
                    value={values?.gender}
                    onChange={(e) => setFieldValue("gender", e?.target.value)}
                  >
                    <MenuItem value={"Male"}>{t("thead_male")}</MenuItem>
                    <MenuItem value={"Female"}>{t("thead_female")}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                <Typography className={t("class-name-field-title")}>
                  {t("thead_date_of_birth")}
                </Typography>
                <SelectDatePicker
                  value={values?.dateOfBirth}
                  onChange={(val) => setFieldValue("dateOfBirth", val)}
                />
                {!!errors?.dateOfBirth && touched?.dateOfBirth && (
                  <FormHelperText error id="outlined-adornment-password">
                    {errors?.dateOfBirth}
                  </FormHelperText>
                )}
              </Grid>

              <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                <Typography className={t("class-name-field-title")}>
                  {t("thead_phone_number")}
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  className="text-field"
                  placeholder={t("thead_phone_number")}
                  value={values?.phoneNumber}
                  onChange={(e) => {
                    const formatted = formatPhoneNumber(e.target.value);
                    setFieldValue("phoneNumber", formatted);
                  }}
                  error={Boolean(touched.phoneNumber && errors.phoneNumber)}
                  helperText={touched.phoneNumber && errors.phoneNumber}
                />
              </Grid>

              <Grid item xs={12}>
                <Typography className={t("class-name-field-title")}>
                  {t("thead_email")}
                </Typography>
                <TextField
                  className="text-field"
                  size="small"
                  fullWidth
                  placeholder={t("thead_email")}
                  {...getFieldProps("email")}
                  error={Boolean(touched.email && errors.email)}
                  helperText={touched.email && errors.email}
                />
              </Grid>

              <Grid item xs={12}>
                <Typography className={t("class-name-field-title")}>
                  {t("thead_remark")}
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  size="small"
                  className="text-field"
                  placeholder={t("thead_remark")}
                  {...getFieldProps("remark")}
                />
              </Grid>

              
            </Grid>
          </Form>
        </FormikProvider>
      </DialogContent>
      <DialogActions sx={{ padding: 3 }}>
        {loading ? (
          <Button className={t("class-btn-create")} fullWidth>
            {t("loading")}
          </Button>
        ) : (
          <Button
            className={t("class-btn-create")}
            fullWidth
            onClick={handleSubmit}
          >
            {dialogTitle === "Update" ? t("update") : t("create")}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
